import React from 'react';
import { Button } from 'antd';
import { HeartOutlined, HeartFilled } from '@ant-design/icons';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT } from '../../../util/types';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../../util/fieldHelpers';
import { getUserTypeFieldInputs } from '../../../util/userHelpers';
import { isValidURL, stripUrl } from '../../../util/urlHelpers';

import {
  H2,
  H3,
  AvatarExtraLarge,
  AvatarLarge,
  NamedLink,
  CustomExtendedDataSection,
} from '../../../components';
import { ExpandableBio } from '../../ListingPage/UserCard/UserCard';

import CustomListingFields from '../../ListingPage/CustomListingFields';
import SectionMapMaybe from '../../ListingPage/SectionMapMaybe';

import css from '../ProfilePage.module.css';

const PRONOUNS_NOT_LISTED = 'not-listed';
const PREFER_NOT_TO_SAY_PRONOUNS = 'prefer-not-to-say';

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl, isPublicProfile = true } = props;

  const userType = publicData?.userType;
  const isBrandAdmin = metadata?.isBrandAdmin || false;

  const isAllowedOnProfile = fieldConfig =>
    getUserTypeFieldInputs(userType, fieldConfig.key, isBrandAdmin, false, isPublicProfile);

  const shouldPickUserField = fieldConfig =>
    fieldConfig?.scope === 'public' &&
    fieldConfig?.showConfig?.displayInProfile !== false &&
    isAllowedOnProfile(fieldConfig);

  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const profileUserType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(profileUserType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (displayInProfile && isTargetUserType && value !== null && isAllowedOnProfile(config)) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

export function CreativeProfileInfo({ listing, config, intl }) {
  const { publicData, metadata, geolocation } = listing?.attributes || {};
  const listingId = listing?.id;
  const listingConfig = config.listing;
  const listingFieldConfigs = listingConfig.listingFields;
  const categoryConfiguration = config.categoryConfiguration;
  const mapsConfig = config.maps;
  return (
    <>
      <CustomListingFields
        publicData={publicData}
        metadata={metadata}
        listingFieldConfigs={listingFieldConfigs}
        categoryConfiguration={categoryConfiguration}
        intl={intl}
      />
      <SectionMapMaybe
        geolocation={geolocation}
        publicData={publicData}
        listingId={listingId}
        mapsConfig={mapsConfig}
      />
    </>
  );
}

export function UserProfileInfo({ user, config, intl }) {
  const { publicData, metadata } = user?.attributes?.profile || {};
  const { userFields } = config.user;
  return (
    <CustomUserFields
      publicData={publicData}
      metadata={metadata}
      userFieldConfig={userFields}
      intl={intl}
      isPublicProfile
    />
  );
}

function ProfileInfo({
  user,
  creativeProfile,
  queryInProgress,
  showLinkToProfileSettingsPage,
  onToggleFavorites,
  isFavorite,
  config,
}) {
  const userId = user?.id?.uuid;
  const { bio, displayName, publicData } = user?.attributes?.profile || {};
  const { publicData: creativePublicData } = creativeProfile?.attributes || {};
  const { userFields } = config.user;
  const { listingFields } = config.listing;
  const pickFieldValue = (key, publicData, config) => {
    if (!publicData) return null;
    const value = publicData[key];
    const fieldConfig = config.find(field => field.key === key);
    const { schemaType, enumOptions } = fieldConfig;
    const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
    switch (schemaType) {
      case SCHEMA_TYPE_ENUM: {
        const selectedOption = findSelectedOption(value)?.label;
        return selectedOption || null;
      }
      case SCHEMA_TYPE_MULTI_ENUM: {
        const parsedArray = value.slice(0, 3);
        const selectedOptions = parsedArray.map(enumValue => findSelectedOption(enumValue)?.label);
        return !!selectedOptions ? selectedOptions.join(', ') : null;
      }

      case SCHEMA_TYPE_TEXT:
      default:
        return value;
    }
  };
  const rawPronouns = publicData?.pronouns;
  const pronouns = pickFieldValue('pronouns', publicData, userFields);
  const showPronouns =
    rawPronouns !== PREFER_NOT_TO_SAY_PRONOUNS && rawPronouns !== PRONOUNS_NOT_LISTED;
  const portfolioURL = pickFieldValue('portfolioURL', publicData, userFields);
  const creativeSpecialty = pickFieldValue('creativeSpecialty', creativePublicData, listingFields);
  const address = creativeProfile?.attributes?.publicData?.location?.address || '';
  const hasBio = !!bio;
  const hasPortfolioURL = !!portfolioURL;
  const parsedPortfolioURL = `${isValidURL(portfolioURL) ? '' : 'http://'}${portfolioURL}`;
  // stripUrl throws without a URL.
  const parsedPortfolioLabel = hasPortfolioURL ? stripUrl(portfolioURL) : '';
  const parsedBookingFormURL = `https://theluupe.typeform.com/booking#creatorname=${displayName}&creatorid=${userId}`;

  const toggleFavorites = () => onToggleFavorites(isFavorite);
  const favoriteButton = isFavorite ? (
    <Button
      type="text"
      icon={<HeartFilled style={{ fontSize: '30px' }} />}
      onClick={toggleFavorites}
      className={css.favoriteButton}
    />
  ) : (
    <Button
      type="text"
      icon={<HeartOutlined style={{ fontSize: '30px' }} />}
      onClick={toggleFavorites}
      className={css.favoriteButton}
    />
  );

  return (
    <div className={css.profileInfoRoot}>
      <div className={css.profileInfoAvatar}>
        <AvatarExtraLarge user={user} disableProfileLink className={css.avatarDesktop} />

        <div className={css.profileInfoWithAvatarMobile}>
          <AvatarLarge user={user} disableProfileLink className={css.avatarMobile} />
          <div className={css.profileInfoContentMobile}>
            <H2 as="h1" className={css.mobileSellerHeading}>
              {displayName}
            </H2>
            {queryInProgress ? (
              <H3>
                <FormattedMessage id="ProfilePage.loadingProfile" />
              </H3>
            ) : (
              <>
                {showPronouns && <span className={css.pronounsLabel}>({pronouns})</span>}
                <p className={css.addressLabel}>{address}</p>
              </>
            )}
          </div>
        </div>

        <div className={css.actionsContainer}>
          {showLinkToProfileSettingsPage ? (
            <>
              <NamedLink className={css.mobileOnly} name="ProfileSettingsPage">
                <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
              </NamedLink>
              <NamedLink className={css.desktopOnly} name="ProfileSettingsPage">
                <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
              </NamedLink>
            </>
          ) : (
            <>
              <Button type="primary" className={css.actionButton} href={parsedBookingFormURL}>
                <FormattedMessage id="ProfilePage.requestToBook" />
              </Button>
              {favoriteButton}
            </>
          )}
        </div>
      </div>

      <div className={css.profileInfoContent}>
        <div className={css.desktopOnly}>
          <H2 as="h1" className={css.desktopSellerHeading}>
            {displayName}
          </H2>
          {!queryInProgress && showPronouns && (
            <span className={css.pronounsLabel}>({pronouns})</span>
          )}
        </div>
        {queryInProgress ? (
          <H3 as="h2">
            <FormattedMessage id="ProfilePage.loadingProfile" />
          </H3>
        ) : (
          <>
            <p className={css.creativeSpecialtyLabel}>{creativeSpecialty}</p>
            <p className={classNames(css.addressLabel, css.desktopOnly)}>{address}</p>
            {hasBio && <ExpandableBio className={css.bioLabel} bio={bio} />}
            {/* The portfolio website, labelled (spec/v0/hide-portfolio.md §3.10). Chosen over a
                "View portfolio" button next to "Request to book". */}
            {hasPortfolioURL && (
              <Button
                type="link"
                target="_blank"
                rel="noopener noreferrer"
                href={parsedPortfolioURL}
                className={css.portfolioLink}
              >
                <span className={css.portfolioLinkText}>
                  <span className={css.portfolioLinkLabel}>
                    <FormattedMessage id="ProfilePage.portfolioWebsiteLabel" />
                  </span>
                  {` · ${parsedPortfolioLabel}`}
                </span>
                <span aria-hidden="true">↗</span>
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
export default ProfileInfo;
